import * as React from 'react'
import { connect } from 'react-redux'
import * as _ from 'underscore'
import { TidewaysContext } from './app/context'
import { Report } from './domain/Report'
import Link from './helper/link'
import Milliseconds from './helper/milliseconds'
import { RangeSelectorInterface } from './history/RangeSelectorInterface'
import { ApplicationState } from './redux/ApplicationState'
import { SERVICE_ALL } from './domain/Service'
import { WithEnvironmentAndService } from './helper/WithEnvironmentAndService'

interface OwnProps {
    data?: Report<any> | null
    selector: RangeSelectorInterface
    showGraph: boolean
    allServices: boolean
    children: React.ReactNode
    resizeReevaluationThreshold: number
}

interface StateProps {
    tidewaysContext: TidewaysContext
}

type Props = StateProps & OwnProps

interface State {
    compactServices: boolean
    compactEnvironments: boolean
}

interface Nodes {
    leftCol?: HTMLElement
}

export class ServiceEnvironmentNavigationComponent extends React.Component<Props, State> {
    public static defaultProps = {
        showGraph: true,
        resizeReevaluationThreshold: 12,
    }

    public state: State = {
        compactServices: false,
        compactEnvironments: false,
    }

    private nodes: Nodes = {}
    private lastWidth: number | null = null
    private layoutCheckFrame: number | null = null

    public render() {
        return (
            <WithEnvironmentAndService
                render={({ service, environment }) => (
                    <div className="row">
                        <div className="col-lg-12" ref={element => this.addDomNode('leftCol', element)}>
                            <ul className="nav nav-pills" style={{ float: 'left', marginRight: '2em', marginBottom: '0' }} role="menu">
                                <li className="nav-label">
                                    <span>
                                        Service{' '}
                                        <Link route="XhprofProfilerBundle.Service.list">
                                            <i className="fa fa-cog text-muted" />
                                        </Link>
                                    </span>
                                </li>
                                {this.state.compactServices
                                    ? this.renderServiceDropdown(this.getServicesList(), service)
                                    : this.renderServiceList(this.getServicesList(), service)}
                            </ul>
                            <ul className="nav nav-pills" style={{ float: 'left', marginBottom: '0' }} role="menu">
                                <li className="nav-label">
                                    <span>
                                        Environment{' '}
                                        <Link route="XhprofProfilerBundle.Environment.list">
                                            <i className="fa fa-cog text-muted" />
                                        </Link>
                                    </span>
                                </li>
                                {this.state.compactEnvironments
                                    ? this.renderEnvironmentDropdown(this.props.tidewaysContext.environments, environment)
                                    : this.renderEnvironmentList(this.props.tidewaysContext.environments, environment)}
                            </ul>
                            {this.props.tidewaysContext.application?.showLimitsReached ? (
                                <ul className="nav nav-pills" style={{ float: 'left', marginBottom: '0', marginLeft: '2em' }} role="menu">
                                    <li>
                                        <Link route="XhprofOrganizationBundle.Application.license">
                                            <i className="fa fa-warning" /> Limits Reached
                                        </Link>
                                    </li>
                                </ul>
                            ) : null}
                            {this.props.tidewaysContext.application?.updateRecommended &&
                            !this.props.tidewaysContext.application?.showLimitsReached ? (
                                <ul className="nav nav-pills" style={{ float: 'left', marginBottom: '0', marginLeft: '2em' }} role="menu">
                                    <li>
                                        <Link route="XhprofProfilerBundle.Server.list">
                                            <i className="fa fa-warning" /> Outdated Versions
                                        </Link>
                                    </li>
                                </ul>
                            ) : null}

                            <div>{this.props.children}</div>
                        </div>
                    </div>
                )}
            />
        )
    }

    private getServicesList = () => this.getServiceIds()

    private getServiceIds = () => {
        const serviceIds = { ...this.props.tidewaysContext.services.serviceIds }

        if (this.props.allServices) {
            serviceIds[SERVICE_ALL] = 0
        }

        return serviceIds
    }

    private serviceDisplayName = (serviceId: string) => (serviceId === SERVICE_ALL ? 'All' : serviceId)

    private renderEnvironmentList = (items, active) =>
        _.map(items, name => (
            <li key={name} className={name === active ? 'active' : undefined}>
                <a className="nav-link" onClick={() => this.props.selector.setEnvironment(name)}>
                    {name}
                </a>
            </li>
        ))

    private renderEnvironmentDropdown = (items, active) => (
        <li className="dropdown">
            <a className="dropdown-toggle" data-toggle="dropdown" href="#">
                {active} <span className="caret" />
            </a>
            <ul className="dropdown-menu">{this.renderEnvironmentList(items, active)}</ul>
        </li>
    )

    private renderServiceList = (items, active) =>
        _.map(items, (id, name) => {
            return (
                <li key={name} className={name === active ? 'active' : undefined}>
                    <a className="nav-link" onClick={() => this.props.selector.setService(name)}>
                        {this.serviceDisplayName(name)}&nbsp;
                        {this.props.showGraph && this.props.data && name !== SERVICE_ALL ? (
                            <Milliseconds value={this.props.data.slices[name] ? this.props.data.slices[name].responseTime * 1000 : 0} />
                        ) : null}
                    </a>
                </li>
            )
        })

    private renderServiceDropdown = (items, active) => (
        <li className="dropdown">
            <a className="dropdown-toggle" data-toggle="dropdown" href="#">
                {this.serviceDisplayName(active)}&nbsp;
                {this.props.showGraph && this.props.data && active !== SERVICE_ALL ? (
                    <Milliseconds value={this.props.data.slices[active] ? this.props.data.slices[active].responseTime * 1000 : 0} />
                ) : null}
                <span className="caret" />
            </a>
            <ul className="dropdown-menu">{this.renderServiceList(items, active)}</ul>
        </li>
    )

    private addDomNode = (key, element) => {
        if (!element) {
            return
        }

        this.nodes[key] = element

        this.checkForLayoutWrap()
    }
    public componentDidMount() {
        if (typeof window === 'undefined') {
            return
        }

        this.lastWidth = window.innerWidth
        window.addEventListener('resize', this.handleResize)
        this.scheduleLayoutCheck()
    }

    public componentDidUpdate() {
        this.scheduleLayoutCheck()
    }

    public componentWillUnmount() {
        if (typeof window === 'undefined') {
            return
        }

        if (this.layoutCheckFrame !== null) {
            window.cancelAnimationFrame(this.layoutCheckFrame)
            this.layoutCheckFrame = null
        }

        window.removeEventListener('resize', this.handleResize)
    }

    private scheduleLayoutCheck = () => {
        if (typeof window === 'undefined') {
            return
        }

        if (this.layoutCheckFrame !== null) {
            window.cancelAnimationFrame(this.layoutCheckFrame)
        }

        this.layoutCheckFrame = window.requestAnimationFrame(() => {
            this.layoutCheckFrame = null
            this.checkForLayoutWrap()
        })
    }

    private handleResize = () => {
        if (typeof window === 'undefined') {
            return
        }

        const width = window.innerWidth
        const threshold = this.props.resizeReevaluationThreshold

        if (this.lastWidth !== null && Math.abs(width - this.lastWidth) < threshold) {
            return
        }

        this.lastWidth = width
        this.setState({ compactServices: false, compactEnvironments: false }, this.checkForLayoutWrap)
    }

    private checkForLayoutWrap = () => {
        if (!this.nodes.leftCol) {
            return
        }

        if (this.state.compactEnvironments && this.state.compactServices) {
            return
        }

        if (!this.hasWrapped(this.nodes.leftCol)) {
            return
        }

        if (!this.state.compactEnvironments) {
            this.setState({ compactEnvironments: true }, this.checkForLayoutWrap)
            return
        }

        this.setState({ compactServices: true }, this.checkForLayoutWrap)
    }

    private hasWrapped = (element: HTMLElement) => {
        const children = (Array.from(element.children) as HTMLElement[]).filter(child => {
            const style = window.getComputedStyle(child)

            if (style.display === 'none' || style.visibility === 'hidden') {
                return false
            }

            return child.childElementCount > 0 || !!child.textContent?.trim()
        })

        if (children.length <= 1) {
            return false
        }

        const firstTop = this.getChildTop(children[0])
        return children.some(child => this.getChildTop(child) - firstTop > 1)
    }

    private getChildTop = (child: HTMLElement) => {
        const rightAlignedChild = child.classList.contains('pull-right')
            ? child
            : (child.querySelector('.pull-right') as HTMLElement | null)

        return (rightAlignedChild || child).getBoundingClientRect().top
    }
}

export default connect<StateProps>((globalState: ApplicationState, props: OwnProps) => {
    return {
        tidewaysContext: globalState.tidewaysContext,
    }
})(ServiceEnvironmentNavigationComponent)
